import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { Feature, FeatureCollection } from "geojson";
import mapValues from "lodash/mapValues";
import pickBy from "lodash/pickBy";
import uniq from "lodash/uniq";
import { MapDataEvent, VectorSource } from "mapbox-gl";
import { useThrottledCallback } from "use-debounce";

import {
  selectAllVersionLayers,
  useGetLayerFeatureTotalsQuery,
  useGetLayersQuery,
  useGetVersionConfigQuery,
  useLazyGetLayerFeatureTotalsQuery,
} from "fond/api";
import { MapContext } from "fond/map/MapProvider";
import { getTypeCountByResolutionState } from "fond/redux/comments";
import { EnrichedMultiProject, Layer, Maybe, Project, Store } from "fond/types";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { filterFeatureCollection, isUuidV4 } from "fond/utils";
import { isMultiProject } from "fond/utils/project";

interface UseFeatureTotalsProps {
  project: Maybe<Project | EnrichedMultiProject>;
  versionId?: string;
}

interface UseFeatureTotalsReturns {
  isLoadingFeatureTotals: boolean;
  loadLayerFeatureTotals: (layerConfig: LayerConfig) => void;
  featureTotals: FeatureTotals;
}

type FeatureTotals = {
  [key: string]: { count: number | null; length: number | null };
};

const useFeatureTotals = ({ versionId, project }: UseFeatureTotalsProps): UseFeatureTotalsReturns => {
  const skipProjectSpecificQueries = !versionId || isMultiProject(project);
  const { map } = useContext(MapContext);
  const [loadFeatureTotals, { isFetching: isLoadingFeatureTotals }] = useLazyGetLayerFeatureTotalsQuery();
  const { data: layerFeatureTotals } = useGetLayerFeatureTotalsQuery(
    { versionId },
    { skip: skipProjectSpecificQueries, refetchOnMountOrArgChange: true }
  );
  const [loadedFeatureTotals, setLoadedFeatureTotals] = useState<FeatureTotals>({});
  const { data: layers } = useGetLayersQuery(versionId ?? skipToken, { skip: skipProjectSpecificQueries });
  const commentLayerCount = useSelector((state: Store) => getTypeCountByResolutionState(state)(state.comments.filters));
  const layerConfigs = useSelector((state: Store) => selectAllVersionLayers(state, versionId));
  const { data: versionConfig } = useGetVersionConfigQuery(versionId ?? skipToken, { skip: !versionId });

  // Various parts of the frontend lie to each other about the existence of certain layers. The backend will get
  // upset if we try to get feature totals for layers that don't actually exist, so limit loading to only those
  // layers that appear plausibly real.
  const realLayers: Record<string, Layer> = useMemo(() => {
    if (!layers) return {};

    const layerConfigurationIds = Object.values(layers.entities)
      .filter((layer): layer is Layer => !!layer?.ID && !!layer?.LayerKey && !!layer?.Version && !!layer?.Configuration?.ID)
      .map((layer) => [layer.Configuration.ID, layer]);

    return Object.fromEntries(layerConfigurationIds);
  }, [layers]);

  // Load feature totals for a layer (typically this is done when the layer has sublayers)
  const loadLayerFeatureTotals = useCallback(
    (layerConfig: LayerConfig) => {
      if (!layerConfig?.ID || !isUuidV4(layerConfig.ID) || layerConfig.Children.length === 0) return;

      loadFeatureTotals({ versionId, layerId: layerConfig.Key }).then((response) => {
        setLoadedFeatureTotals((prev) => ({ ...prev, ...response.data }));
      });
    },
    [loadFeatureTotals, versionId]
  );

  const [mapboxFeatureTotals, setMapboxFeatureTotals] = useState<FeatureTotals>({});

  const updateMapboxSources = useThrottledCallback(
    useCallback(() => {
      if (!map || !versionConfig) {
        setMapboxFeatureTotals({});
        return;
      }

      const style = map?.getStyle();
      if (!style) {
        return;
      }

      const geojsonSources: Record<string, FeatureCollection> = {};
      const vectorTileSources: Record<string, VectorSource> = {};
      Object.entries(style.sources).forEach(([sourceId, source]) => {
        if (source.type === "geojson" && "data" in source && source.data) {
          geojsonSources[sourceId] = source.data as FeatureCollection;
        } else if (source.type === "vector") {
          vectorTileSources[sourceId] = source;
        }
      });

      // A given layer/sublayer configuration can give rise to many style configurations, which are transformed
      // into "mapbox layers". Each "mapbox layer" for a given layer/sublayer configuration has the same filter
      // (by construction), so here we're just picking out one filter for each observed layer/sublayer configuration.
      const seenLayerIds = new Map();
      const vectorTileIds = new Set();
      const skip = new Set();

      style.layers?.forEach((layer) => {
        if (!("source" in layer) || !layer?.source || typeof layer.source !== "string") return;

        if (
          // is this layer using a geojson source?
          geojsonSources[layer.source] &&
          // does this layer have the appropriate metadata linking it to a configuration?
          layer.metadata?.itemId &&
          // only consider a layer/sublayer configuration once
          !skip.has(layer.metadata.itemId)
        ) {
          // Don't process another "mapbox layer" for this layer/sublayer configuration.
          skip.add(layer.metadata.itemId);

          // Our goal is to filter down to those layers that don't actually exist (counting the number of features
          // in layers that exist is the backend's job). Because of the various mutations applied to `versionConfig`
          // this _is not_ a simple task at all. We filter out some common cases here.

          // The "mapbox layer" corresponds to a layer configuration associated with a real layer.
          if (layer.metadata.itemId in realLayers) {
            return;
          }

          // The "mapbox layer" corresponds to a sublayer configuration whose parent configuration is associated
          // with a real layer.
          const mapboxLayerConfig = versionConfig.Data.entities[layer.metadata.itemId];
          if (mapboxLayerConfig?.Type === "SUBLAYER" && mapboxLayerConfig.ParentID && mapboxLayerConfig.ParentID in realLayers) {
            return;
          }
          seenLayerIds.set(layer.metadata.itemId, { source: geojsonSources[layer.source], filter: layer.filter ?? null });
        } else if (vectorTileSources[layer.source] && layer?.metadata?.itemId) {
          vectorTileIds.add(layer.metadata.itemId);
        }
      });

      // Count features using the representative filter for each layer/sublayer configuration.
      const layerConfigFeatures: Record<string, Feature[]> = {};
      seenLayerIds.forEach(({ source, filter }, layerConfigId) => {
        layerConfigFeatures[layerConfigId] = filterFeatureCollection(source, filter);
      });

      // If a layer configuration has no styles, then it won't have a "mapbox layer" counting it.
      // In that case, we count it by aggregating all its sublayer features and then counting them.
      layerConfigs.forEach((layerConfig) => {
        if (
          layerConfig.Type !== "LAYER" ||
          layerConfig.ID in layerConfigFeatures ||
          layerConfig.ID in vectorTileIds ||
          layerConfig.Children.length === 0 ||
          // If any sublayer of this configuration is using vector tiles, then we don't
          // want to count this layer.
          layerConfig.Children.some((sublayerId) => vectorTileIds.has(sublayerId))
        )
          return;

        const features: Feature[] = [];
        layerConfig.Children.forEach((sublayerId) => {
          const sublayerConfig = versionConfig.Data.entities[sublayerId];
          if (!sublayerConfig) return;

          features.push(...(layerConfigFeatures?.[sublayerConfig.ID] ?? []));
        });

        // Sublayers are allowed to have duplicate elements (this happens for cost2serve layers when the buckets
        // collapse), so deduplicate features here (by referential equality).
        layerConfigFeatures[layerConfig.ID] = uniq(features);
      });

      const layerConfigFeatureCounts = mapValues(layerConfigFeatures, (features) => features.length);

      // An edge case here is empty layers. An empty layer may not be present in `realLayers`, which means the above
      // will end up counting the features in that layer. That could be fine, but when this happens to a line layer
      // (which uses lengths instead of feature counts) it looks a little strange. TO avoid this, we filter out
      // any empty feature counts that look like they come from an empty layer. This is only a heuristic, but it's
      // the best we've got without a serious rework in our versionConfig handling.
      const featureCounts = pickBy(layerConfigFeatureCounts, (value, key) => value > 0 || !isUuidV4(key));
      setMapboxFeatureTotals(mapValues(featureCounts, (featureCount) => ({ count: featureCount, length: null })));
    }, [map, versionConfig, layerConfigs, realLayers]),
    500
  );

  // When source data changes, update our map-based geojson counts. This happens fairly frequently
  // (e.g. when scrolling the map), so throttle any resulting updates.
  useEffect(() => {
    if (!map) return;

    const handleData = (e: MapDataEvent) => {
      if (e.dataType !== "source" || !e.isSourceLoaded || e.source?.type !== "geojson" || !e.sourceId) return;
      updateMapboxSources();
    };

    map.on("sourcedata", handleData);
    return () => {
      map.off("sourcedata", handleData);
    };
  }, [map, updateMapboxSources]);

  // Assemble the feature totals from the various sources. This should roughly be in order from least
  // trustworthy to most trustworthy.
  const featureTotals = useMemo(
    () => ({ ...mapboxFeatureTotals, ...layerFeatureTotals, ...loadedFeatureTotals, ...commentLayerCount }),
    [mapboxFeatureTotals, layerFeatureTotals, loadedFeatureTotals, commentLayerCount]
  );

  return useMemo(
    () => ({ featureTotals, loadLayerFeatureTotals, isLoadingFeatureTotals }),
    [featureTotals, loadLayerFeatureTotals, isLoadingFeatureTotals]
  );
};

export default useFeatureTotals;
